import api from './api';

export interface VisitorRecord {
  // 基础关联信息
  id: number;
  link_id: number;
  client_id: number;
  user_id?: number;
  link_code: string;
  link_title?: string;
  request_url?: string;
  redirect_url: string;
  redirect_type: 'default' | 'intercept';
  rule_match_result?: string;

  // 访客请求信息
  visitor_ip: string;
  user_agent: string;
  referer?: string;
  accept_language?: string;
  client_language?: string;
  accessed_at: string;
  ds?: string;

  // 地理信息
  country_code?: string;
  country_name?: string;
  region?: string;
  city?: string;
  postal_code?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;

  // 设备与客户端
  device_type?: string;
  device_brand?: string;
  device_model?: string;
  os_type?: string;
  os_version?: string;
  browser_type?: string;
  browser_version?: string;
  client_type?: string;
  mobile_app?: string;
  is_mobile: boolean;

  // 网络画像
  asn?: string;
  asn_name?: string;
  asn_type?: string;
  asn_route?: string;
  asn_org?: string;
  is_datacenter?: boolean;
  is_proxy?: boolean;
  is_satellite?: boolean;
  is_vpn?: boolean;
  is_tor?: boolean;
  is_relay?: boolean;
  proxy_type?: string;
  risk_level?: string;
  network_type?: string;
  ip_domain?: string;
  ip_hostname?: string;
  hosted_domains_count?: number;
  is_bot?: boolean;
  bot_type?: string;

  // 企业/滥用信息
  company_domain?: string;
  company_type?: string;
  company_address?: string;
  abuse_contact_name?: string;
  abuse_contact_email?: string;
  abuse_contact_phone?: string;
  abuse_contact_address?: string;

  // 拦截信息
  is_intercepted: boolean;
  intercept_reason?: string;
  intercept_type?: string;

  // 其他附加字段
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  ip_info_extra?: Record<string, any>;
  response_code?: number;
  processing_time?: number;
  
  // 广告平台和规则
  ad_plat?: string;           // 广告平台
  rule_id?: number;           // 规则ID

  // APP包名
  x_requested_with?: string;  // X-Requested-With 请求头，APP包名

  // 数据来源标记
  use_ipcore?: number;        // 是否使用 IPCore 服务，0: 否，1: 是
  use_fingerprint?: number;   // 是否使用 Fingerprintjs，0: 否，1: 是
  fingerprint_id?: string;    // 指纹ID，只有使用指纹接口时才有值
}

export interface VisitorListRequest {
  page?: number;
  limit?: number;
  last_id?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
  website_group?: string;
  shortCode?: string;
  country?: string;
  access_status?: string;
  vpn_proxy?: string;
  datacenter?: string;
  satellite?: string;
  show_advanced?: boolean;
  ip_risk_level?: string;
  ip_location?: string;
  network_type?: string;
  device_type?: string;
  device_system?: string;
  client_type?: string;
  client_language?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  link_id?: number; // 添加链接ID参数
  requestUrl?: string;
  visitorIp?: string;
}

export interface VisitorListResponse {
  list: VisitorRecord[];
  total?: number;
  page?: number;
  limit?: number;
  size?: number;
  next_last_id?: number;
  has_more?: boolean;
}

// 访客记录API服务
export const visitorService = {
  // 获取访客记录列表
  getVisitorRecords: async (params: VisitorListRequest): Promise<VisitorListResponse> => {
    const response = await api.post('/api/visitors/records', params);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 获取访客记录详情
  getVisitorRecordDetail: async (id: number): Promise<VisitorRecord> => {
    const response = await api.post('/api/visitors/records/detail', { id });
    return response.data.data; // 修复：正确访问 data.data
  },

  // 导出访客记录
  exportVisitorRecords: async (params: VisitorListRequest): Promise<void> => {
    await api.post('/api/visitors/export', params);
  },

  // 获取访客记录统计
  getVisitorStats: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/visitors', params);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 获取IP访问统计
  getIPStats: async (params: { search?: string; dateRange?: string[]; start_date?: string; end_date?: string; page?: number; size?: number; }): Promise<{list: any[]; total: number; page: number; size: number;}> => {
    const response = await api.post('/api/visitors/ip-stats', params);
    return response.data.data;
  },

  // IP 黑白名单管理
  addIPToList: async (data: { ip: string; type: 'whitelist'|'blacklist'; link_id?: number; note?: string; }): Promise<number> => {
    const response = await api.post('/api/visitors/ip-list/add', data);
    return response.data.data?.id;
  },
  listIPList: async (data: { type?: 'whitelist'|'blacklist'; link_id?: number; page?: number; size?: number; }): Promise<{list: any[]; total: number; page: number; size: number;}> => {
    const response = await api.post('/api/visitors/ip-list/list', data);
    return response.data.data;
  },
  deleteIPFromList: async (id: number): Promise<void> => {
    await api.post('/api/visitors/ip-list/delete', { id });
  },

  // 获取地理位置分布
  getGeographyDistribution: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/geography', params);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 获取设备分布
  getDeviceDistribution: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/devices', params);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 获取网络分布
  getNetworkDistribution: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/networks', params);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 获取UTM参数统计
  getUtmStats: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/utm', params);
    return response.data.data; // 修复：正确访问 data.data
  },
}; 